import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, Alert, Link as MuiLink } from '@mui/material';
import api from '../api/axiosConfig';
import Footer from '../components/Footer';

// Google Analytics gtag function and window property typings
const GA_MEASUREMENT_ID = 'G-56R16ZWNK6';
function gtag(...args: any[]) {
  (window as any).dataLayer.push(args);
}
function injectGtag() {
  if (!(window as any).gtagScriptInjected) {
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID);
    (window as any).gtagScriptInjected = true;
  }
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const response = await api.post('/api/auth/login', {
        email,
        password,
      });
      const token = response.data.token?.access_token;
      if (token) {
        localStorage.setItem('jwt', token);
        localStorage.setItem('email', email);
        navigate('/');
      } else {
        setError('Invalid response from server.');
      }
    } catch (err: any) {
      let message = 'Login failed. Please check your credentials.';
      if (err.response?.status === 403) {
        message = 'Incorrect password. Please try again.';
      } else if (err.response?.data?.ErrorMessage) {
        message = err.response.data.ErrorMessage;
      } else if (err.message) {
        message = err.message;
      }
      setError(message);
    }
  };

  useEffect(() => {
    injectGtag();
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      width="100vw"
      sx={{ background: '#fff' }}
    >
      {/* Main content wrapper */}
      <Box
        display="flex"
        flex={1}
        width="100%"
      >
        {/* Left image section */}
        <Box
          flex={1}
          display={{ xs: 'none', md: 'flex' }}
          alignItems="center"
          justifyContent="center"
          sx={{
            background: 'linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)',
            minHeight: 'calc(100vh - 60px)',
            width: '50vw',
            p: 0,
            m: 0,
            overflow: 'hidden',
            position: { md: 'fixed' },
            left: 0,
            top: 0,
            bottom: 60,
            height: 'calc(100vh - 60px)',
            zIndex: 1,
          }}
        >
          <img
            src="/paas.png"
            alt="Prediction as a Service"
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 0, boxShadow: 'none' }}
            onError={e => { (e.target as HTMLImageElement).src = '/logo_black.png'; }}
          />
        </Box>
        {/* Right sign-in section */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={{ xs: '100vw', md: '50vw' }}
          minHeight="calc(100vh - 60px)"
          sx={{ background: '#fff', ml: { md: '50vw' }, zIndex: 2 }}
        >
          <Paper elevation={3} sx={{ p: 6, maxWidth: 500, width: '100%' }}>
            <Box display="flex" justifyContent="center" mb={2}>
              <img src="/logo_black.png" alt="Logo" style={{ height: '50px' }} />
            </Box>
            <Typography variant="h5" mb={2} align="center">Sign-In to Prediction as a Service</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <form onSubmit={handleSubmit}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                fullWidth
                required
                margin="normal"
                sx={{
                  '.MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                required
                margin="normal"
                sx={{
                  '.MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
                Sign-In to Prediction as a Service
              </Button>
            </form>
            <Button variant="outlined" color="primary" fullWidth sx={{ mt: 2 }} onClick={() => alert('Google sign-in coming soon!')}>
              Sign in with Google
            </Button>
            <Box display="flex" justifyContent="space-between" mt={2}>
              <MuiLink component={Link} to="/signup" variant="body2" underline="hover">
                Don't have an account? Sign up
              </MuiLink>
              <MuiLink component={Link} to="/forgot-password" variant="body2" underline="hover">
                Forgot password?
              </MuiLink>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default Login;